import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { collectHistoricalStatusData, historicalStatusDataKey } from "./collectHistoricalStatusData";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { QueryDiagnosticDeviceData } from "@/models/QueryDiagnosticDeviceData";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/**
 * Returns historical status data for devices with diagnostic IDs within a specified time range.
 * @param diagnosticIds of the diagnostics we want to fetch
 * @param deviceIds of the devices, the data is needed
 * @param fromDate starting date of which the data is needed
 * @param interval for refetching data (optional). `false` asks once, which is what a caller
 * looking at a period that has already ended wants.
 * @returns - Historical Status data for the devices with the dates when the status data was recorded
 */
export function useHistoricalStatusData(
  diagnosticIds: string[],
  deviceIds: string[],
  fromDate: Date,
  toDate?: Date,
  interval?: number | false,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery({
    // The requested window is part of the identity of this query. Without it, moving the date
    // range served the previously cached range instead of refetching.
    queryKey: [
      "historicalStatusData",
      activeSessionId,
      diagnosticIds,
      deviceIds,
      fromDate.getTime(),
      toDate?.getTime() ?? null,
    ],

    queryFn: async ({ signal }): Promise<QueryDiagnosticDeviceData> => {
      // One Get per (diagnostic, device) pair, issued together so the client batches them into
      // ExecuteMultiCall requests, and a truncated page is paged forward instead of being cut
      // short.
      const collector = collectHistoricalStatusData(diagnosticIds, deviceIds);

      const tasks = diagnosticIds.flatMap(diagnosticId =>
        deviceIds.map(deviceId => ({
          key: historicalStatusDataKey(diagnosticId, deviceId),
          diagnosticId,
          deviceId,
        })),
      );

      await fetchStatusDataComplete(geotabClient, {
        tasks,
        fromDate,
        toDate: toDate ?? new Date(),
        onPage: collector.addPage,
        signal,
      });

      return collector.result;
    },

    refetchInterval: interval ?? 30000,
    placeholderData: keepPreviousData,
  });
}
